/**
 * Step components for the Terminal Reader order flow.
 * Extracted from the terminals.order route for file size reduction.
 */

import { formatCentsAsCurrency } from "@plasma/utils/money-formatters";
import { CheckCircle2, CreditCard, Truck } from "lucide-react";

import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";

export interface HardwareSKU {
  id: string;
  product: string;
  productName: string;
  country: string;
  price: number;
  currency: string;
  status: string;
}

export interface ShippingMethod {
  id: string;
  name: string;
  deliveryEstimate: {
    minimum: { value: number; unit: string };
    maximum: { value: number; unit: string };
  } | null;
  amount: number;
  currency: string;
  status: string;
}

export interface OrderPreview {
  items: {
    sku: string;
    productName: string;
    quantity: number;
    unitAmount: number;
    totalAmount: number;
  }[];
  subtotal: number;
  shippingAmount: number;
  taxAmount: number;
  totalAmount: number;
  currency: string;
  shippingMethod: string;
  shippingMethodName: string;
}

export function formatDeliveryEstimate(
  estimate: ShippingMethod["deliveryEstimate"]
): string {
  if (!estimate) {
    return "Varies";
  }
  const min = estimate.minimum.value;
  const max = estimate.maximum.value;
  if (min === max) {
    return `${min} business days`;
  }
  return `${min}-${max} business days`;
}

/**
 * Step 1: Reader selection content
 */
export function SelectReaderStep({
  skus,
  shippingMethods,
  selectedSku,
  setSelectedSku,
  quantity,
  setQuantity,
  selectedShipping,
  setSelectedShipping,
  location,
}: {
  skus: HardwareSKU[];
  shippingMethods: ShippingMethod[];
  selectedSku: string;
  setSelectedSku: (v: string) => void;
  quantity: number;
  setQuantity: (v: number) => void;
  selectedShipping: string;
  setSelectedShipping: (v: string) => void;
  location:
    | {
        name: string;
        address?: {
          addressLine1?: string | null;
          city?: string | null;
          state?: string | null;
        } | null;
      }
    | null
    | undefined;
}) {
  const selectedSkuData = skus.find((s) => s.id === selectedSku);
  const selectedShippingData = shippingMethods.find(
    (m) => m.id === selectedShipping
  );

  return (
    <div className="grid gap-4 py-4">
      <div className="grid gap-2">
        <Label htmlFor="reader">Card Reader Model</Label>
        <Select onValueChange={setSelectedSku} value={selectedSku}>
          <SelectTrigger id="reader">
            <SelectValue placeholder="Select a card reader" />
          </SelectTrigger>
          <SelectContent>
            {skus.map((sku) => (
              <SelectItem key={sku.id} value={sku.id}>
                <span className="flex w-full items-center justify-between gap-4">
                  <span>{sku.productName}</span>
                  <span className="text-muted-foreground">
                    {formatCentsAsCurrency(sku.price)}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedSkuData && (
          <p className="text-muted-foreground text-sm">
            {selectedSkuData.productName === "Stripe Reader S700"
              ? "Countertop card reader with customer-facing display"
              : "Portable Bluetooth card reader for mobile payments"}
          </p>
        )}
      </div>

      <div className="grid gap-2">
        <Label htmlFor="quantity">Quantity</Label>
        <Select
          onValueChange={(v) => setQuantity(Number.parseInt(v, 10))}
          value={quantity.toString()}
        >
          <SelectTrigger id="quantity">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[1, 2, 3, 4, 5].map((n) => (
              <SelectItem key={n} value={n.toString()}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="shipping">Shipping Method</Label>
        <Select onValueChange={setSelectedShipping} value={selectedShipping}>
          <SelectTrigger id="shipping">
            <SelectValue placeholder="Select shipping" />
          </SelectTrigger>
          <SelectContent>
            {shippingMethods.map((method) => (
              <SelectItem key={method.id} value={method.id}>
                <span className="flex w-full items-center justify-between gap-4">
                  <span>{method.name}</span>
                  <span className="text-muted-foreground">
                    {method.amount === 0
                      ? "Free"
                      : formatCentsAsCurrency(method.amount)}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedShippingData && (
          <p className="text-muted-foreground flex items-center gap-1 text-sm">
            <Truck className="size-3" />
            Delivery:{" "}
            {formatDeliveryEstimate(selectedShippingData.deliveryEstimate)}
          </p>
        )}
      </div>

      {location && (
        <div className="bg-muted/50 mt-2 rounded-lg border p-3">
          <p className="text-sm font-medium">Shipping to:</p>
          <p className="text-muted-foreground text-sm">
            {location.name}
            <br />
            {location.address?.addressLine1}
            <br />
            {location.address?.city}, {location.address?.state}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Step 2: Order preview content
 */
export function OrderPreviewStep({ preview }: { preview: OrderPreview }) {
  return (
    <div className="py-4">
      <div className="space-y-4">
        {/* Items */}
        <div className="divide-y rounded-lg border">
          {preview.items.map((item) => (
            <div className="flex justify-between p-3" key={item.productName}>
              <div>
                <p className="font-medium">{item.productName}</p>
                <p className="text-muted-foreground text-sm">
                  Qty: {item.quantity}
                </p>
              </div>
              <p className="font-medium">
                {formatCentsAsCurrency(item.totalAmount)}
              </p>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="space-y-2 rounded-lg border p-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span>{formatCentsAsCurrency(preview.subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              Shipping ({preview.shippingMethodName})
            </span>
            <span>
              {preview.shippingAmount === 0
                ? "Free"
                : formatCentsAsCurrency(preview.shippingAmount)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Tax</span>
            <span>{formatCentsAsCurrency(preview.taxAmount)}</span>
          </div>
          <div className="flex justify-between border-t pt-2 font-medium">
            <span>Total</span>
            <span>{formatCentsAsCurrency(preview.totalAmount)}</span>
          </div>
        </div>

        {/* Payment notice */}
        <div className="bg-info/10 dark:bg-info/10 flex items-start gap-2 rounded-lg p-3 text-sm">
          <CreditCard className="text-info dark:text-info mt-0.5 size-4 shrink-0" />
          <p className="text-info dark:text-info/70">
            Your payment method on file will be charged{" "}
            {formatCentsAsCurrency(preview.totalAmount)} upon confirmation.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Step 3: Success content
 */
export function OrderSuccessStep({
  orderResult,
}: {
  orderResult: { orderId?: string; stripeOrderId?: string } | null;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="bg-success/10 dark:bg-success/15 mb-4 flex size-16 items-center justify-center rounded-full">
        <CheckCircle2 className="text-success dark:text-success size-8" />
      </div>
      <h3 className="text-lg font-semibold">Order Placed!</h3>
      <p className="text-muted-foreground mt-1 max-w-sm text-sm">
        Your reader order has been placed and you'll receive tracking
        information once it ships.
      </p>
      {orderResult?.stripeOrderId && (
        <p className="text-muted-foreground mt-3 font-mono text-xs">
          Order ID: {orderResult.stripeOrderId}
        </p>
      )}
    </div>
  );
}
